import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export type SiteSettings = {
    eventcount: number;
    video_url: string;
    color: string;
    maintenance_active: boolean;
    maintenance_text: string;
    maintenance_show_date: boolean;
    domain: string;
    mail: string;
    saniphone: string;
    address_name: string;
    address_street: string;
    address_city: string;
    address_zipcode: string;
    address_maps_link: string;
    parking_label: string;
    parking_url: string;
    tshirts_woman: string[];
    tshirts_men: string[];
    date_start: string;
    date_end: string;
    registration_fee: number;
    registration_max: number;
    show_registration_status: boolean;
    registration_last_register: string;
    registration_last_cancel: string;
    payment_reminder_days: number;
    bank_account_holder: string;
    bank_iban: string;
    bank_remittance: string;
    document_logo_original_name: string;
};

export type MediaArchiveLink = {
    id: number;
    title: string;
    url: string;
    sort_order: number;
};

export type MediaArchiveYear = {
    id: number;
    year: number;
    subtitle: string | null;
    sort_order: number;
    links: MediaArchiveLink[];
};

export type MediaArchiveOverview = {
    years: MediaArchiveYear[];
};

export type MediaArchiveYearAdd = {
    year: number;
    subtitle?: string | null;
};

export type MediaArchiveYearUpdate = MediaArchiveYearAdd;

export type MediaArchiveLinkAdd = {
    title: string;
    url: string;
};

export type MediaArchiveLinkUpdate = MediaArchiveLinkAdd;

export type LocalizedText = {
    de: string;
    en: string;
    fr: string;
};

export type WhatToExpectSection = {
    id: number;
    title: LocalizedText;
    text: LocalizedText;
    icon: string;
    sort_order: number;
};

export type WhatToExpectSectionSummary = {
    id: number;
    title: LocalizedText;
    icon: string;
    sort_order: number;
};

export type WhatToExpectOverview = {
    sections: WhatToExpectSectionSummary[];
};

export type WhatToExpectSectionAdd = {
    title: LocalizedText;
    text: LocalizedText;
    icon: string;
};

export type WhatToExpectSectionUpdate = WhatToExpectSectionAdd;

export class SettingsService {
    public static getSiteSettingsApiV1SettingsGet(): CancelablePromise<SiteSettings> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/settings',
        });
    }

    public static updateSiteSettingsApiV1SettingsPut(data: {
        requestBody: SiteSettings;
    }): CancelablePromise<SiteSettings> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/settings',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    public static async getDocumentLogoBlob(): Promise<Blob> {
        const url = `${OpenAPI.BASE}/api/v1/settings/document-logo`;
        const headers = OpenAPI.HEADERS;
        const authHeaders =
            headers && typeof headers !== 'function' ? {...headers} : {};
        const response = await fetch(url, {
            method: 'GET',
            headers: authHeaders,
            credentials: OpenAPI.CREDENTIALS,
        });
        if (!response.ok) {
            throw new Error(`Failed to load document logo (${response.status})`);
        }
        return response.blob();
    }

    public static uploadDocumentLogoApiV1SettingsDocumentLogoPost(data: {
        formData: {file: Blob};
    }): CancelablePromise<SiteSettings> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/settings/document-logo',
            formData: data.formData,
            mediaType: 'multipart/form-data',
        });
    }

    public static deleteDocumentLogoApiV1SettingsDocumentLogoDelete(): CancelablePromise<SiteSettings> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/settings/document-logo',
        });
    }

    public static getArchiveOverviewApiV1SettingsArchiveGet(): CancelablePromise<MediaArchiveOverview> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/settings/archive',
        });
    }

    public static addArchiveYearApiV1SettingsArchiveYearsPost(data: {
        requestBody: MediaArchiveYearAdd;
    }): CancelablePromise<MediaArchiveYear> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/settings/archive/years',
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static updateArchiveYearApiV1SettingsArchiveYearsYearIdPut(data: {
        yearId: number;
        requestBody: MediaArchiveYearUpdate;
    }): CancelablePromise<MediaArchiveYear> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/settings/archive/years/{year_id}',
            path: {year_id: data.yearId},
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static deleteArchiveYearApiV1SettingsArchiveYearsYearIdDelete(data: {
        yearId: number;
    }): CancelablePromise<MediaArchiveYear> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/settings/archive/years/{year_id}',
            path: {year_id: data.yearId},
        });
    }

    public static addArchiveLinkApiV1SettingsArchiveYearsYearIdLinksPost(data: {
        yearId: number;
        requestBody: MediaArchiveLinkAdd;
    }): CancelablePromise<MediaArchiveLink> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/settings/archive/years/{year_id}/links',
            path: {year_id: data.yearId},
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static updateArchiveLinkApiV1SettingsArchiveLinksLinkIdPut(data: {
        linkId: number;
        requestBody: MediaArchiveLinkUpdate;
    }): CancelablePromise<MediaArchiveLink> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/settings/archive/links/{link_id}',
            path: {link_id: data.linkId},
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static deleteArchiveLinkApiV1SettingsArchiveLinksLinkIdDelete(data: {
        linkId: number;
    }): CancelablePromise<MediaArchiveLink> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/settings/archive/links/{link_id}',
            path: {link_id: data.linkId},
        });
    }

    public static reorderArchiveLinksApiV1SettingsArchiveYearsYearIdLinksReorderPut(data: {
        yearId: number;
        requestBody: {ids: number[]};
    }): CancelablePromise<MediaArchiveYear> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/settings/archive/years/{year_id}/links/reorder',
            path: {year_id: data.yearId},
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static getWhatToExpectOverviewApiV1SettingsWhatToExpectGet(): CancelablePromise<WhatToExpectOverview> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/settings/what-to-expect',
        });
    }

    public static getWhatToExpectSectionApiV1SettingsWhatToExpectSectionsSectionIdGet(data: {
        sectionId: number;
    }): CancelablePromise<WhatToExpectSection> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/settings/what-to-expect/sections/{section_id}',
            path: {section_id: data.sectionId},
        });
    }

    public static addWhatToExpectSectionApiV1SettingsWhatToExpectSectionsPost(data: {
        requestBody: WhatToExpectSectionAdd;
    }): CancelablePromise<WhatToExpectSection> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/settings/what-to-expect/sections',
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static updateWhatToExpectSectionApiV1SettingsWhatToExpectSectionsSectionIdPut(data: {
        sectionId: number;
        requestBody: WhatToExpectSectionUpdate;
    }): CancelablePromise<WhatToExpectSection> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/settings/what-to-expect/sections/{section_id}',
            path: {section_id: data.sectionId},
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }

    public static deleteWhatToExpectSectionApiV1SettingsWhatToExpectSectionsSectionIdDelete(data: {
        sectionId: number;
    }): CancelablePromise<WhatToExpectSection> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/settings/what-to-expect/sections/{section_id}',
            path: {section_id: data.sectionId},
        });
    }

    public static reorderWhatToExpectSectionsApiV1SettingsWhatToExpectSectionsReorderPut(data: {
        requestBody: {ids: number[]};
    }): CancelablePromise<WhatToExpectOverview> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/settings/what-to-expect/sections/reorder',
            body: data.requestBody,
            mediaType: 'application/json',
        });
    }
}
